import React from "react";
import PropTypes from "prop-types";
import DollarIcon from "../../../../assets/img/action-center/dollar.svg";
import { gapBarWidths } from "./impactBandUtils";

/**
 * Renders an Action Centre v2 card's impact band (EO-1217) from the backend's render-ready
 * `impact` object (parsed `impact_json`) and `impactBandType`.
 *
 * Band types:
 *  - own_dollar / own_pct / peer_proven → dollar icon + eyebrow (label) + big value_text +
 *    methodology footnote. The icon is the same for every band — the design spec is explicit:
 *    "Green box - dollar sign for all" — so it is not keyed off the band type.
 *  - market_gap → a two-bar you-vs-benchmark comparison (impact.gap), plus the eyebrow/footnote.
 * `none` (or a missing impact) renders nothing — the parent gates on that.
 *
 * NOTE (2026-08, EO-1273): the market_gap branch below is currently unreachable — LlmCard's
 * HIDDEN_BANDS gates market_gap out on a business call, and LlmCard is this component's only
 * caller. It is kept rather than deleted because the gate is a product decision that may be
 * reversed; ImpactBand.test.jsx still covers the branch. Delete both if the decision sticks.
 *
 * That branch has NO dollar icon, which contradicts "dollar sign for all". Left alone rather
 * than guessed at: it renders bars, not a value line, so where the icon sits is a layout
 * question and there is no Figma frame for the v2 market_gap variant to answer it. If the gate
 * is ever lifted, get that frame and add the icon before shipping it.
 */
const ImpactBand = ({ type, impact }) => {
  if (!impact || type === "none") return null;

  const label = impact.label;
  const source = impact.source_text;

  if (type === "market_gap" && impact.gap) {
    const g = impact.gap;
    const unit = g.unit || "";
    const widths = gapBarWidths(g);

    return (
      <div className="llm-card-impact llm-card-impact--gap">
        {label && <span className="impact-label">{label}</span>}
        <div className="impact-bars">
          <div className="impact-bar-row">
            <span className="impact-bar-name">{g.you_label || "You"}</span>
            <span className="impact-bar-track">
              <span
                className="impact-bar impact-bar--you"
                style={{ "--acb-bar-width": widths.you }}
              />
            </span>
            <span className="impact-bar-value">
              {g.you}
              {unit}
            </span>
          </div>
          <div className="impact-bar-row">
            <span className="impact-bar-name">
              {g.benchmark_label || "Benchmark"}
            </span>
            <span className="impact-bar-track">
              <span
                className="impact-bar impact-bar--benchmark"
                style={{ "--acb-bar-width": widths.benchmark }}
              />
            </span>
            <span className="impact-bar-value">
              {g.benchmark}
              {unit}
            </span>
          </div>
        </div>
        {source && <span className="impact-source">{source}</span>}
      </div>
    );
  }

  return (
    <div className={`llm-card-impact llm-card-impact--${type}`}>
      <span className="impact-icon">
        <img src={DollarIcon} alt="" />
      </span>
      <span className="impact-body">
        {label && <span className="impact-label">{label}</span>}
        {impact.value_text && (
          <span className="impact-value">{impact.value_text}</span>
        )}
        {source && <span className="impact-source">{source}</span>}
      </span>
    </div>
  );
};

ImpactBand.propTypes = {
  // own_dollar | own_pct | peer_proven | market_gap | none
  type: PropTypes.string,
  // Render-ready band: { label, value_text, source_text, amount, amount_pct, gap }
  impact: PropTypes.shape({
    label: PropTypes.string,
    value_text: PropTypes.string,
    source_text: PropTypes.string,
    gap: PropTypes.shape({
      you: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      benchmark: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      you_label: PropTypes.string,
      benchmark_label: PropTypes.string,
      unit: PropTypes.string,
    }),
  }),
};

export default ImpactBand;
